/**
 * Usage Page - Detailed storage and photo breakdown by project and view
 */

import { useState, useEffect } from 'react';
import { Button, Card, Drawer, Dropdown, Grid, Modal, Progress, Skeleton, Space, Table, Tabs, Tag, Typography, message } from 'antd';
import type { MenuProps } from 'antd';
import AppLayout from '@/components/AppLayout';
import { MaterialIcon } from '@/components/MaterialIcon';
import { useStorageQuota } from '@/hooks/useStorageQuota';
import { useEffectiveUserId } from '@/hooks/useEffectiveUserId';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getCountFromServer } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { getProjects, deleteProject } from '@/services/projectService';
import { getViewsByProject, deleteView, updateTimerSettings, updateCameraSettings } from '@/services/viewService';
import { Project, View, TimerSettings, CameraSettings } from '@/types';
import { TimerTab } from '@/components/TimerTab';
import { CameraTab } from '@/components/CameraTab';
import { useConnectionStatus } from '@/hooks/useConnectionStatus';
import { TestShotViewer } from '@/components/TestShotViewer';
import { MODAL_BLUR_STYLES } from '@/styles/modalStyles';

const { Title, Text } = Typography;

/**
 * Format bytes to human-readable string
 */
const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const gb = bytes / (1024 * 1024 * 1024);
  if (gb >= 1) return `${gb.toFixed(2)} GB`;
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  const kb = bytes / 1024;
  if (kb >= 1) return `${kb.toFixed(0)} KB`;
  return `${bytes} B`;
};

/**
 * Get progress bar color based on percentage
 */
const getStrokeColor = (pct: number): string => {
  if (pct >= 90) return '#ff4d4f';
  if (pct >= 70) return '#faad14';
  return '#1677ff';
};

interface ProjectWithViews {
  project: Project;
  views: View[];
  totalBytes: number;
  totalPhotos: number;
}

function isTestShotDisabledDslr(view: View): boolean {
  const isDslrMode = view.settings?.camera?.mode === 'dslr';
  const isDslrConnected = view.androidStatus?.device?.dslr?.connected ?? false;
  return isDslrMode && !isDslrConnected;
}

export default function UsagePage() {
  const { effectiveUid } = useEffectiveUserId();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const { usedBytes, totalBytes, percentage, planName, loading: quotaLoading } = useStorageQuota();
  const [projectsData, setProjectsData] = useState<ProjectWithViews[]>([]);
  const [rawCounts, setRawCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);

  // Settings drawer state
  const [settingsDrawerVisible, setSettingsDrawerVisible] = useState(false);
  const [selectedView, setSelectedView] = useState<View | null>(null);
  const selectedViewStatus = useConnectionStatus(selectedView?.token, selectedView?.androidStatus);
  const [activeDrawerTab, setActiveDrawerTab] = useState<string>('1');
  const [timerSaveHandler, setTimerSaveHandler] = useState<(() => Promise<void>) | null>(null);
  const [timerHasChanges, setTimerHasChanges] = useState<boolean>(false);
  const [timerCancelHandler, setTimerCancelHandler] = useState<(() => void) | null>(null);
  const [timerIsSaving, setTimerIsSaving] = useState(false);
  const [testShotView, setTestShotView] = useState<View | null>(null);

  useEffect(() => {
    if (!effectiveUid) return;

    const loadData = async () => {
      setLoading(true);
      try {
        const projects = await getProjects(effectiveUid);
        const results: ProjectWithViews[] = [];

        for (const project of projects) {
          const views = await getViewsByProject(effectiveUid, project.projectId);
          const totalBytes = views.reduce((sum, v) => sum + (v.stats?.storageBytes ?? (v.stats?.storageMB ?? 0) * 1024 * 1024), 0);
          const totalPhotos = views.reduce((sum, v) => sum + (v.stats?.photoCount ?? 0), 0);
          results.push({ project, views, totalBytes, totalPhotos });
        }

        // Sort by storage descending
        results.sort((a, b) => b.totalBytes - a.totalBytes);
        setProjectsData(results);

        // Load RAW counts in background (non-blocking)
        const allViews = results.flatMap(r => r.views);
        loadRawCounts(effectiveUid, allViews);
      } catch (error) {
        console.error('Failed to load usage data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [effectiveUid]);

  // Count RAW photos per view.
  // Uses a lightweight query counting docs where rawUrl exists.
  const loadRawCounts = async (userId: string, views: View[]) => {
    const counts: Record<string, number> = {};
    await Promise.all(
      views.map(async (view) => {
        try {
          // Query photos for this view, only fetch rawUrl field
          const q = query(
            collection(db, 'photos'),
            where('userId', '==', userId),
            where('viewId', '==', view.viewId),
          );
          const snap = await getCountFromServer(q);
          const totalCount = snap.data().count;

          // Only fetch individual docs if view has photos (to count raw)
          if (totalCount === 0) {
            counts[view.viewId] = 0;
            return;
          }

          // For views with photos, query with rawUrl filter
          // This requires a composite index - if it fails, fallback to 0
          try {
            const rawQ = query(
              collection(db, 'photos'),
              where('userId', '==', userId),
              where('viewId', '==', view.viewId),
              where('rawUrl', '!=', null),
            );
            const rawSnap = await getCountFromServer(rawQ);
            counts[view.viewId] = rawSnap.data().count;
          } catch {
            // Index not ready yet - fallback: mark as unknown
            counts[view.viewId] = 0;
          }
        } catch {
          counts[view.viewId] = 0;
        }
      })
    );
    setRawCounts(counts);
  };

  // Reload data
  const reloadData = async () => {
    if (!effectiveUid) return;
    setLoading(true);
    try {
      const projects = await getProjects(effectiveUid);
      const results: ProjectWithViews[] = [];
      for (const project of projects) {
        const views = await getViewsByProject(effectiveUid, project.projectId);
        const totalBytes = views.reduce((sum, v) => sum + (v.stats?.storageBytes ?? (v.stats?.storageMB ?? 0) * 1024 * 1024), 0);
        const totalPhotos = views.reduce((sum, v) => sum + (v.stats?.photoCount ?? 0), 0);
        results.push({ project, views, totalBytes, totalPhotos });
      }
      results.sort((a, b) => b.totalBytes - a.totalBytes);
      setProjectsData(results);
    } catch (error) {
      console.error('Failed to reload usage data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Save timer settings
  const handleSaveTimerSettings = async (capturing: boolean, timerSettings?: TimerSettings) => {
    if (!effectiveUid || !selectedView) return;
    try {
      await updateTimerSettings(effectiveUid, selectedView.projectId, selectedView.viewId, capturing, timerSettings);
    } catch (error) {
      console.error('Failed to save timer settings:', error);
      throw error;
    }
  };

  // Save camera settings
  const handleSaveCameraSettings = async (cameraSettings: CameraSettings) => {
    if (!effectiveUid || !selectedView) return;
    try {
      await updateCameraSettings(effectiveUid, selectedView.projectId, selectedView.viewId, cameraSettings);
    } catch (error) {
      console.error('Failed to save camera settings:', error);
      throw error;
    }
  };

  const handleCopyToken = async (token: string) => {
    try {
      await navigator.clipboard.writeText(token);
      message.success('Token copied to clipboard');
    } catch {
      message.error('Failed to copy token');
    }
  };

  const closeSettingsDrawer = () => {
    setSettingsDrawerVisible(false);
    setSelectedView(null);
    setActiveDrawerTab('1');
    setTimerSaveHandler(null);
    setTimerHasChanges(false);
    setTimerCancelHandler(null);
  };

  const handleDeleteView = (userId: string, projectId: string, viewId: string, viewName: string) => {
    Modal.confirm({
      title: 'Delete View',
      content: `Are you sure you want to delete "${viewName}"? All photos will be deleted.`,
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteView(userId, projectId, viewId);
          message.success('View deleted');
          reloadData();
        } catch (error) {
          console.error('Failed to delete view:', error);
          message.error('Failed to delete view');
        }
      },
    });
  };

  const handleDeleteProject = (projectId: string, projectName: string) => {
    Modal.confirm({
      title: 'Delete Project',
      content: `Are you sure you want to delete "${projectName}"? All views and photos will be deleted.`,
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteProject(effectiveUid!, projectId);
          message.success('Project deleted');
          reloadData();
        } catch (error) {
          console.error('Failed to delete project:', error);
          message.error('Failed to delete project');
        }
      },
    });
  };

  const getViewMenu = (view: View): MenuProps['items'] => [
    {
      key: 'settings',
      icon: <MaterialIcon name="settings" />,
      label: 'Settings',
      onClick: () => {
        setSelectedView(view);
        setSettingsDrawerVisible(true);
      },
    },
    {
      key: 'photos',
      icon: <MaterialIcon name="photo_library" />,
      label: 'Photos',
      onClick: () => navigate(`/photos/${view.viewId}`),
    },
    { type: 'divider' },
    {
      key: 'delete',
      icon: <MaterialIcon name="delete" />,
      label: 'Delete View',
      danger: true,
      onClick: () => handleDeleteView(view.userId, view.projectId, view.viewId, view.name),
    },
  ];

  const getProjectMenu = (project: Project): MenuProps['items'] => [
    {
      key: 'open',
      icon: <MaterialIcon name="open_in_new" />,
      label: 'Open Project',
      onClick: () => navigate(`/?projectId=${project.projectId}`),
    },
    { type: 'divider' },
    {
      key: 'delete',
      icon: <MaterialIcon name="delete" />,
      label: 'Delete Project',
      danger: true,
      onClick: () => handleDeleteProject(project.projectId, project.name),
    },
  ];

  const totalPhotos = projectsData.reduce((sum, p) => sum + p.totalPhotos, 0);

  const viewColumns = [
    {
      title: 'View',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => <Text strong>{name}</Text>,
    },
    {
      title: 'Photos',
      dataIndex: ['stats', 'photoCount'],
      key: 'photos',
      width: 100,
      align: 'right' as const,
      render: (count: number) => (count ?? 0).toLocaleString(),
    },
    {
      title: 'RAW',
      key: 'raw',
      width: 80,
      align: 'right' as const,
      render: (_: unknown, view: View) => {
        const count = rawCounts[view.viewId];
        if (count === undefined) return <Text type="secondary">—</Text>;
        if (count === 0) return <Text type="secondary">0</Text>;
        return <Text>{count.toLocaleString()}</Text>;
      },
    },
    {
      title: 'Storage',
      key: 'storage',
      width: 120,
      align: 'right' as const,
      render: (_: unknown, view: View) => {
        const bytes = view.stats?.storageBytes ?? (view.stats?.storageMB ?? 0) * 1024 * 1024;
        return formatBytes(bytes);
      },
    },
    {
      title: '',
      key: 'bar',
      width: 150,
      render: (_: unknown, view: View) => {
        const bytes = view.stats?.storageBytes ?? (view.stats?.storageMB ?? 0) * 1024 * 1024;
        const pct = totalBytes > 0 ? Math.round((bytes / totalBytes) * 100) : 0;
        return (
          <Progress
            percent={pct}
            showInfo={false}
            size={['100%', 8]}
            strokeColor="#1677ff"
          />
        );
      },
    },
    {
      title: '',
      key: 'actions',
      width: 48,
      render: (_: unknown, view: View) => (
        <Dropdown menu={{ items: getViewMenu(view) }} trigger={['click']} placement="bottomRight">
          <Button type="text" size="small" icon={<MaterialIcon name="more_vert" size={18} />} />
        </Dropdown>
      ),
    },
  ];

  // Single reveal: quota + projects/views load behind ONE skeleton shaped
  // like the real page (summary cards + project cards), no piecewise flashes.
  // Both gates are primary data sources, so no forced reveal: showing the
  // page without them would render misleading empty states.
  // RAW counts intentionally arrive after reveal — their column shows "—".
  const pageReady = !loading && !quotaLoading;

  if (!pageReady) {
    return (
      <AppLayout>
        <div style={{ maxWidth: 900, margin: '0 auto', padding: 24 }}>
          <Title level={2}>Usage Details</Title>
          <Skeleton
            active
            title={false}
            paragraph={{ rows: 1, width: 260 }}
            style={{ marginBottom: 24 }}
          />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 32 }}>
            {[0, 1, 2].map((i) => (
              <Card size="small" key={i}>
                <Skeleton active title={false} paragraph={{ rows: 3 }} />
              </Card>
            ))}
          </div>
          <Title level={4} style={{ marginBottom: 16 }}>By Project</Title>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[0, 1].map((i) => (
              <Card key={i}>
                <Skeleton active title={false} paragraph={{ rows: 4 }} />
              </Card>
            ))}
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div style={{ maxWidth: 900, margin: '0 auto', padding: 24 }}>
        {/* Header */}
        <Title level={2}>Usage Details</Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
          Storage and photo breakdown for your <strong>{planName}</strong> plan
        </Text>

        {/* Summary cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 32 }}>
          {/* Storage card */}
          <Card size="small">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <MaterialIcon name="cloud" size={20} style={{ color: '#1677ff' }} />
              <Text type="secondary">Storage</Text>
            </div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>
              {formatBytes(usedBytes)}
            </div>
            <Text type="secondary" style={{ fontSize: 13 }}>
              of {formatBytes(totalBytes)}
            </Text>
            <Progress
              percent={percentage}
              showInfo={false}
              strokeColor={getStrokeColor(percentage)}
              size={['100%', 8]}
              style={{ marginTop: 8 }}
            />
            <Text style={{ fontSize: 13, color: getStrokeColor(percentage), fontWeight: 600 }}>
              {percentage}% used
            </Text>
          </Card>

          {/* Photos card */}
          <Card size="small">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <MaterialIcon name="photo_library" size={20} style={{ color: '#52c41a' }} />
              <Text type="secondary">Total Photos</Text>
            </div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>
              {totalPhotos.toLocaleString()}
            </div>
            <Text type="secondary" style={{ fontSize: 13 }}>
              across {projectsData.length} project{projectsData.length !== 1 ? 's' : ''}
            </Text>
          </Card>

          {/* Views card */}
          <Card size="small">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <MaterialIcon name="videocam" size={20} style={{ color: '#faad14' }} />
              <Text type="secondary">Active Views</Text>
            </div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>
              {projectsData.reduce((sum, p) => sum + p.views.length, 0)}
            </div>
            <Text type="secondary" style={{ fontSize: 13 }}>
              across {projectsData.length} project{projectsData.length !== 1 ? 's' : ''}
            </Text>
          </Card>
        </div>

        {/* Per-project breakdown */}
        <Title level={4} style={{ marginBottom: 16 }}>By Project</Title>

        {projectsData.length === 0 ? (
          <Card>
            <div style={{ textAlign: 'center', padding: 32, color: 'rgba(0,0,0,0.45)' }}>
              <MaterialIcon name="folder_off" size={48} style={{ color: '#d9d9d9' }} />
              <p>No projects yet</p>
            </div>
          </Card>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {projectsData.map(({ project, views, totalBytes: projBytes, totalPhotos: projPhotos }) => {
              const projPct = totalBytes > 0 ? Math.round((projBytes / totalBytes) * 100) : 0;
              return (
                <Card key={project.projectId}>
                  {/* Project header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <MaterialIcon name="folder" size={22} style={{ color: '#1677ff' }} />
                      <Text strong style={{ fontSize: 16 }}>{project.name}</Text>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Text strong>{formatBytes(projBytes)}</Text>
                      <Text type="secondary">
                        {projPhotos.toLocaleString()} photo{projPhotos !== 1 ? 's' : ''}
                        {(() => {
                          const projRaw = views.reduce((sum, v) => sum + (rawCounts[v.viewId] ?? 0), 0);
                          return projRaw > 0 ? ` (${projRaw.toLocaleString()} RAW)` : '';
                        })()}
                      </Text>
                      <Dropdown menu={{ items: getProjectMenu(project) }} trigger={['click']} placement="bottomRight">
                        <Button type="text" size="small" icon={<MaterialIcon name="more_vert" size={18} />} />
                      </Dropdown>
                    </div>
                  </div>

                  {/* Project progress */}
                  <div style={{ marginBottom: 16 }}>
                    <Progress
                      percent={projPct}
                      showInfo={false}
                      strokeColor="#1677ff"
                      size={['100%', 10]}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {projPct}% of total storage
                    </Text>
                  </div>

                  {/* Views table */}
                  {views.length > 0 ? (
                    <Table
                      dataSource={views.sort((a, b) => {
                        const aBytes = a.stats?.storageBytes ?? (a.stats?.storageMB ?? 0) * 1024 * 1024;
                        const bBytes = b.stats?.storageBytes ?? (b.stats?.storageMB ?? 0) * 1024 * 1024;
                        return bBytes - aBytes;
                      })}
                      columns={viewColumns}
                      rowKey="viewId"
                      pagination={false}
                      size="small"
                    />
                  ) : (
                    <Text type="secondary" style={{ fontStyle: 'italic' }}>No views in this project</Text>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Test Shot Viewer */}
      {testShotView && (
        <TestShotViewer
          view={testShotView}
          onClose={() => setTestShotView(null)}
        />
      )}

      {/* Device Settings Drawer */}
      <Drawer
        title={
          <div>
            <div>View: {selectedView?.name || ''}</div>
            <Space size="small" style={{ marginTop: 4 }}>
              {selectedView?.token && (
                <Tag variant="outlined" style={{ cursor: 'pointer' }} title="Click to copy token" onClick={() => handleCopyToken(selectedView.token)}>
                  <MaterialIcon name="key" size={14} style={{ marginRight: 4 }} />
                  {selectedView.token}
                </Tag>
              )}
              <Tag variant="outlined">
                <MaterialIcon name="phone_android" size={14} style={{ marginRight: 4 }} />
                {selectedView?.androidStatus?.device?.model || 'No device'}
              </Tag>
            </Space>
          </div>
        }
        placement="right"
        size="large"
        width={isMobile ? '100%' : undefined}
        open={settingsDrawerVisible}
        closeIcon={null}
        extra={
          <Button
            type="default"
            icon={<MaterialIcon name="close" size={20} />}
            onClick={closeSettingsDrawer}
          />
        }
        onClose={closeSettingsDrawer}
        styles={MODAL_BLUR_STYLES}
        footer={
          activeDrawerTab === '1' && selectedView ? (
            <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
              <Button
                type="primary"
                onClick={() => {
                  setTestShotView(selectedView);
                  setSettingsDrawerVisible(false);
                }}
                size="large"
                icon={<MaterialIcon name="photo_camera" size={18} />}
                disabled={
                  selectedViewStatus !== 'online' ||
                  isTestShotDisabledDslr(selectedView)
                }
              >
                Test Shot
              </Button>
            </div>
          ) : activeDrawerTab === '2' && timerSaveHandler ? (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', width: '100%' }}>
              <Button
                onClick={timerCancelHandler || undefined}
                size="large"
                disabled={!timerHasChanges || timerIsSaving}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={timerSaveHandler}
                size="large"
                loading={timerIsSaving}
                disabled={!timerHasChanges}
              >
                Save timer settings
              </Button>
            </div>
          ) : null
        }
      >
        <Tabs
          activeKey={activeDrawerTab}
          onChange={(key) => {
            setActiveDrawerTab(key);
            const contentHolder = document.querySelector('.ant-drawer-body .ant-tabs-content-holder');
            if (contentHolder) {
              contentHolder.scrollTop = 0;
            }
            if (key !== '2') {
              setTimerSaveHandler(null);
              setTimerHasChanges(false);
              setTimerCancelHandler(null);
            }
          }}
          items={[
            {
              key: '1',
              label: 'Camera',
              icon: <MaterialIcon name="photo_camera" size={22} />,
              children: selectedView ? (
                <CameraTab
                  view={selectedView}
                  onSave={handleSaveCameraSettings}
                  hideTestShotButton={true}
                />
              ) : (
                <Text>No view selected</Text>
              ),
            },
            {
              key: '2',
              label: 'Timer',
              icon: <MaterialIcon name="avg_pace" size={22} />,
              children: selectedView ? (
                <TimerTab
                  view={selectedView}
                  onSave={async (capturing, settings) => {
                    // No settings means the capture toggle, which writes `capturing`
                    // alone and spins its own switch — it must not spin Save.
                    const isFormSave = settings !== undefined;
                    if (isFormSave) setTimerIsSaving(true);
                    try {
                      await handleSaveTimerSettings(capturing, settings);
                    } finally {
                      if (isFormSave) setTimerIsSaving(false);
                    }
                  }}
                  onSaveHandlerReady={(handler, hasChanges, cancelHandler) => {
                    setTimerSaveHandler(() => handler);
                    setTimerHasChanges(hasChanges);
                    setTimerCancelHandler(() => cancelHandler);
                  }}
                  hideSaveButton={true}
                />
              ) : (
                <Text>No view selected</Text>
              ),
            },
          ]}
        />
      </Drawer>
    </AppLayout>
  );
}
